import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useDomainsFavicons } from "app/services/sitesService/hooks/useDomainsFavicons";
import type { IDomainFavicon } from "app/services/sitesService/types";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { AddToDashboardModalV2 } from "pro-features/components/Modals/src/AddToDashboardModal-v2";

import type { IFoldersFilters } from "../../types";

import { FiltersSection } from "./components/FiltersSection";
import { SegmentCreationModal } from "./components/SegmentCreationModal/SegmentCreationModal";
import { TableControls } from "./components/TableControls";
import { useDashboardModal } from "./hooks/useDashboardModal";
import { useRadioFilters } from "./hooks/useRadioFilters";
import { useSegmentCreation } from "./hooks/useSegmentCreation";
import { useTableActions } from "./hooks/useTableActions";

const EMPTY_FAVICONS_LIST: IDomainFavicon[] = [];

interface IFoldersTableTopProps {
    downloadExcelPermitted: boolean;
    excelLink: string;
    initialFilters: IFoldersFilters;
    radioFilters: { isAll: boolean; isNew: boolean; isTrending: boolean };
    onFilterChange: (filters: Partial<IFoldersFilters>) => void;
    currentDomain: string;
    setCurrentDomain: (domain: string) => void;
    selectedFoldersForSegment: string[];
    searchTerm: string;
}

export const FoldersTableTop: FC<IFoldersTableTopProps> = ({
    downloadExcelPermitted,
    excelLink,
    initialFilters,
    radioFilters,
    onFilterChange,
    currentDomain,
    setCurrentDomain,
    selectedFoldersForSegment,
    searchTerm,
}) => {
    const isCompare = isCompareMode();
    const { params } = useCurrentStateAndParams();
    const key = params?.key as string;
    const keysArr = isCompare ? key?.split(",") : [key];

    const { data: faviconsList = EMPTY_FAVICONS_LIST } = useDomainsFavicons(keysArr, {
        enabled: keysArr.length > 0,
    });

    const domainsList = faviconsList.map((item) => ({
        name: item.Domain,
        displayName: item.Domain,
        icon: item.Favicon,
    }));

    const segmentCreation = useSegmentCreation(currentDomain, selectedFoldersForSegment);
    const radioFiltersHook = useRadioFilters(initialFilters, onFilterChange, radioFilters);

    const domainObj = domainsList.find((domain) => domain.name === currentDomain);

    const saveProps = {
        onSave: segmentCreation.onSave,
        isDisabled: !segmentCreation.segmentName.isValidName || segmentCreation.segmentName.assetName?.length === 0,
        isLoading: segmentCreation.isCreatingSegment,
    };

    const onDomainChange = (newKey: { text: string }) => {
        const domainName = newKey.text;
        if (!domainName || domainName === currentDomain) {
            return;
        }

        try {
            setCurrentDomain(domainName);
            onFilterChange({ keys: domainName });
            radioFiltersHook.setFilters((prev: IFoldersFilters) => ({ ...prev, keys: domainName }));
        } catch (error) {
            console.error("Error changing domain:", error);
        }
    };

    const { settingsLocale, excelExportConfig, onSearchChange } = useTableActions({
        downloadExcelPermitted,
        excelLink,
        onFilterChange,
        radioFilters: radioFiltersHook,
    });

    const { isOpen, customModel, handleClose, handleOpenDashboard } = useDashboardModal({
        radioFilters: radioFiltersHook,
        domainObj,
    });
    return (
        <>
            <FiltersSection
                isCompare={isCompare}
                domainsList={domainsList}
                currentDomain={currentDomain}
                onDomainChange={onDomainChange}
                radioFilters={radioFiltersHook}
            />
            <TableControls
                search={searchTerm}
                onSearchChange={onSearchChange}
                settingsLocale={settingsLocale}
                excelExportConfig={excelExportConfig}
                handleOpenDashboard={handleOpenDashboard}
                segmentCreation={segmentCreation}
                selectedRowKeys={selectedFoldersForSegment}
            />
            {isOpen ? (
                <AddToDashboardModalV2
                    customModel={customModel}
                    onConfirm={handleClose}
                    onCancel={handleClose}
                />
            ) : null}
            <SegmentCreationModal
                isOpen={segmentCreation.isCreateNewListModalOpen}
                onCancel={segmentCreation.onCancel}
                saveProps={saveProps}
                segmentName={segmentCreation.segmentName}
                onSegmentNameChange={segmentCreation.onSegmentNameChange}
                isCreatingSegment={segmentCreation.isCreatingSegment}
                popupStep={segmentCreation.popupStep}
                goBack={segmentCreation.goBack}
                analyzeSegment={segmentCreation.analyzeSegment}
            />
        </>
    );
};
